import { For, Show, createMemo } from 'solid-js'

import { useI18n } from '../../i18n'
import {
   currentFast,
   deleteHistoryEntry,
   fastHistory,
   formatFastHistoryTimestamp,
   formatFatBurnDisplay,
   formatHistoryMonthLabel,
   getCurrentFastSnapshot,
   getFatBurnedGrams,
   getFastDurationMs,
   getHistoryCutoffTimestamp,
   getHistoryMonthKey,
   historyMonthsDisplayed,
   historyRemoteState,
   openEditHistoryModal,
   requestMoreHistoryMonths,
} from '../../state/currentFastStore'
import EntryActions from '../ui/EntryActions'

type HistoryMonthItem = {
   type: 'month'
   key: string
   label: string
}

type HistoryEntryItem = {
   type: 'entry'
   key: string
   isCurrent: boolean
   historyIndex: number | null
   startLabel: string
   durationHoursLabel: string
   fatBurnedLabel: string
}

type HistoryRenderableItem = HistoryMonthItem | HistoryEntryItem

/**
 * Renders a month header row in the history list.
 *
 * @param props - The month item.
 * @returns The row element.
 */
const HistoryMonthRow = (props: { item: HistoryMonthItem }) => (
   <li class="lg:col-span-2 text-xs uppercase tracking-wide text-ink-muted border-t border-edge-soft pt-3 mt-3 first:mt-0 first:pt-0 first:border-t-0">
      {props.item.label}
   </li>
)

/**
 * Renders a single fast entry row in the history list.
 *
 * @param props - The entry item.
 * @returns The row element.
 */
const HistoryEntryRow = (props: { item: HistoryEntryItem }) => {
   const t = useI18n()

   return (
      <li
         class="bg-surface-control p-4 rounded-lg flex items-center justify-between lg:h-full"
         classList={{
            'border border-emerald-400/40 shadow-lg shadow-emerald-900/30':
               props.item.isCurrent,
         }}
      >
         <div class="flex-grow">
            <p class="font-bold text-lg">
               {props.item.durationHoursLabel} {t('hours')}
            </p>
            <p class="text-sm text-ink-muted">{props.item.startLabel}</p>
            <p class="text-sm text-ink-muted">
               {t('Fat burned:')} {props.item.fatBurnedLabel}
            </p>
         </div>
         <Show
            when={!props.item.isCurrent}
            fallback={
               <span class="ml-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                  {t('In progress')}
               </span>
            }
         >
            <EntryActions
               onEdit={() => openEditHistoryModal(props.item.historyIndex ?? -1)}
               onDelete={() => deleteHistoryEntry(props.item.historyIndex ?? -1)}
               editLabel={t('Edit Fast')}
            />
         </Show>
      </li>
   )
}

/**
 * Builds the month-grouped rows for the fasts ending after the cutoff, with
 * the in-progress fast first.
 *
 * @param cutoff - Earliest end time to include.
 * @returns The rows to render.
 */
function buildHistoryRows(cutoff: number): HistoryRenderableItem[] {
   const currentSnapshot = getCurrentFastSnapshot()
   const entries = [
      ...(currentSnapshot ?
         [{ fast: currentSnapshot, isCurrent: true, historyIndex: null as number | null }]
      :  []),
      ...fastHistory().map((entry, index) => ({
         fast: entry,
         isCurrent: false,
         historyIndex: index,
      })),
   ]
   const items: HistoryRenderableItem[] = []
   let currentMonthKey = ''
   for (const entry of entries) {
      if (entry.fast.endTime < cutoff) {
         continue
      }
      const monthKey = getHistoryMonthKey(entry.fast.endTime)
      if (monthKey && monthKey !== currentMonthKey) {
         currentMonthKey = monthKey
         items.push({
            type: 'month',
            key: monthKey,
            label: formatHistoryMonthLabel(entry.fast.endTime),
         })
      }
      const durationMs = getFastDurationMs(entry.fast)
      items.push({
         type: 'entry',
         key: `${entry.fast.startTime}-${entry.fast.endTime}-${entry.historyIndex ?? 'current'}`,
         isCurrent: entry.isCurrent,
         historyIndex: entry.historyIndex,
         startLabel: formatFastHistoryTimestamp(entry.fast.startTime),
         durationHoursLabel:
            durationMs > 0 ? (durationMs / (1000 * 60 * 60)).toFixed(1) : '--',
         fatBurnedLabel: formatFatBurnDisplay(getFatBurnedGrams(durationMs)),
      })
   }
   return items
}

/**
 * Renders the grouped, paginated list of past fasts.
 *
 * @returns The history list element.
 */
const HistoryList = () => {
   const t = useI18n()
   const cutoff = createMemo(() => getHistoryCutoffTimestamp(historyMonthsDisplayed()))
   const renderItems = createMemo(() => buildHistoryRows(cutoff()))
   const hasLocalMore = createMemo(() =>
      fastHistory().some(entry => entry.endTime < cutoff()),
   )

   const emptyMessage = createMemo(() => {
      if (historyRemoteState().loading && fastHistory().length === 0 && !currentFast()) {
         return t('Loading history…')
      }
      if (historyRemoteState().errorMessage) {
         return t('Unable to load remote history. Showing saved entries.')
      }
      if (fastHistory().length > 0 || currentFast()) {
         return historyMonthsDisplayed() === 1 ?
               t('No fasts completed in the past month.')
            :  t('No fasts found within the past {count} months.', {
                  count: historyMonthsDisplayed(),
               })
      }
      return t('No past fasts recorded.')
   })

   return (
      <>
         <ul
            id="history-list"
            class="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-4"
         >
            <Show
               when={renderItems().length > 0}
               fallback={
                  <li class="text-center text-ink-faint p-4 lg:col-span-2">
                     {emptyMessage()}
                  </li>
               }
            >
               <For each={renderItems()}>
                  {item =>
                     item.type === 'month' ?
                        <HistoryMonthRow item={item} />
                     :  <HistoryEntryRow item={item} />
                  }
               </For>
            </Show>
         </ul>
         <div
            id="history-load-more-container"
            class="mt-4 flex items-center justify-center"
            classList={{
               hidden: !hasLocalMore() && !historyRemoteState().hasRemoteSync,
            }}
         >
            <button
               id="history-load-more-btn"
               type="button"
               onClick={requestMoreHistoryMonths}
               class="px-4 py-2 rounded-lg border border-edge bg-surface-control text-ink-soft text-sm font-semibold transition hover:bg-surface-control-hover focus:outline-none focus:ring-2 focus:ring-accent-strong"
            >
               {t('Load next month')}
            </button>
         </div>
      </>
   )
}

export default HistoryList
